import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  LockOpen as LockOpenIcon,
  Refresh as RefreshIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';
import { getApiBaseUrl, getStoredToken } from '../../services/httpClient';

function toLocalInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fromLocalInputValue(local) {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function authHeaders() {
  const token = getStoredToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

const BetaGate = () => {
  const apiBase = getApiBaseUrl();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [launchLocal, setLaunchLocal] = useState('');
  const [allowBillUsers, setAllowBillUsers] = useState(true);
  const [allowlist, setAllowlist] = useState([]);
  const [appFilter, setAppFilter] = useState('all');
  const [newApp, setNewApp] = useState('mitra');
  const [newPhone, setNewPhone] = useState('');
  const [newNote, setNewNote] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${apiBase}/early-access/admin/config`, {
        headers: authHeaders(),
      });
      if (!data?.success) throw new Error(data?.message || 'Load failed');
      const cfg = data.data || {};
      setEnabled(cfg.enabled === true);
      setLaunchLocal(toLocalInputValue(cfg.launch_at));
      setAllowBillUsers(cfg.allow_citizen_bill_users !== false);
      setAllowlist(Array.isArray(cfg.allowlist) ? cfg.allowlist : []);
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [apiBase]);

  useEffect(() => {
    load();
  }, [load]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(
        `${apiBase}/early-access/admin/config`,
        {
          enabled,
          launch_at: fromLocalInputValue(launchLocal),
          allow_citizen_bill_users: allowBillUsers,
        },
        { headers: authHeaders() },
      );
      if (!data?.success) throw new Error(data?.message || 'Save failed');
      toast.success('BetaGate settings saved');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addPhone = async () => {
    try {
      const { data } = await axios.post(
        `${apiBase}/early-access/admin/allowlist`,
        { app: newApp, phone: newPhone, note: newNote },
        { headers: authHeaders() },
      );
      if (!data?.success) throw new Error(data?.message || 'Add failed');
      toast.success('Phone added');
      setNewPhone('');
      setNewNote('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Add failed');
    }
  };

  const removePhone = async (id) => {
    try {
      await axios.delete(`${apiBase}/early-access/admin/allowlist/${id}`, {
        headers: authHeaders(),
      });
      toast.success('Removed');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || 'Remove failed');
    }
  };

  const filtered = allowlist.filter(
    (r) => appFilter === 'all' || r.app_key === appFilter,
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <LockOpenIcon color="primary" />
        <Typography variant="h5" fontWeight={700}>
          BetaGate
        </Typography>
        <Chip
          size="small"
          label={enabled ? 'ENABLED' : 'DISABLED'}
          color={enabled ? 'warning' : 'default'}
        />
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Early access for Locsy Mitra &amp; Citizen. When disabled, everyone uses
        the apps normally. When enabled, only allowlisted phones (and optional
        Mitra bill citizens) can enter until launch time.
      </Typography>

      <Paper sx={{ p: 2.5, mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Master controls
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              color="warning"
            />
          }
          label="Early Access Gate enabled"
        />
        <TextField
          fullWidth
          type="datetime-local"
          label="Launch date & time"
          value={launchLocal}
          onChange={(e) => setLaunchLocal(e.target.value)}
          InputLabelProps={{ shrink: true }}
          helperText="After this time (device timezone in this form), apps open to everyone"
          sx={{ mt: 2, mb: 2 }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={allowBillUsers}
              onChange={(e) => setAllowBillUsers(e.target.checked)}
            />
          }
          label="Citizen: allow Mitra bill–created accounts (phone + last-4 PIN)"
        />
        <Alert severity="info" sx={{ mt: 2 }}>
          Mitra access = phones you add below with app = mitra. Citizen access =
          citizen allowlist + optional bill users when the switch above is ON.
        </Alert>
        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveConfig}
            disabled={saving}
          >
            Save settings
          </Button>
          <Button startIcon={<RefreshIcon />} onClick={load}>
            Refresh
          </Button>
        </Stack>
      </Paper>

      <Paper sx={{ p: 2.5 }}>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Allowlist phones
        </Typography>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ mb: 2 }}
          alignItems={{ sm: 'flex-start' }}
        >
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>App</InputLabel>
            <Select
              label="App"
              value={newApp}
              onChange={(e) => setNewApp(e.target.value)}
            >
              <MenuItem value="mitra">Mitra</MenuItem>
              <MenuItem value="citizen">Citizen</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            label="Mobile"
            placeholder="10 digits"
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
            sx={{ flex: 1 }}
          />
          <TextField
            size="small"
            label="Note"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
            sx={{ flex: 1 }}
          />
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addPhone}
            disabled={newPhone.length !== 10}
          >
            Add
          </Button>
        </Stack>

        <Divider sx={{ mb: 2 }} />

        <FormControl size="small" sx={{ mb: 1, minWidth: 140 }}>
          <InputLabel>Filter</InputLabel>
          <Select
            label="Filter"
            value={appFilter}
            onChange={(e) => setAppFilter(e.target.value)}
          >
            <MenuItem value="all">All apps</MenuItem>
            <MenuItem value="mitra">Mitra</MenuItem>
            <MenuItem value="citizen">Citizen</MenuItem>
          </Select>
        </FormControl>

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>App</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Note</TableCell>
                <TableCell align="right">Remove</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography color="text.secondary">No phones yet</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <Chip size="small" label={row.app_key} />
                    </TableCell>
                    <TableCell>{row.phone}</TableCell>
                    <TableCell>{row.note || '—'}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => removePhone(row.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default BetaGate;
